import * as jQuery from "jquery";
import { EDA } from "motw_core/Dreams/EDreamAttribute";
import { hashTagsService } from "motw_core/Services/HashTagsService";
import { sortBy } from "lodash";
import { HandleNewDreamEditorLaunch } from "source/app/presentation/MainPanel/Dreams/Add/Helpers/HandleNewDreamEditorLaunch";
import { LoadAdjacentDreams } from "source/app/presentation/MainPanel/Dreams/DreamFilterButtons/Filters/ShowAdjacent";
import {
    GetAttributeOnlyFilters,
    LoadDreamsWithAttributeFilter,
} from "source/app/presentation/MainPanel/Dreams/DreamFilterButtons/Filters/ShowDreamFilter";
import { LoadDreamsThisWeek } from "source/app/presentation/MainPanel/Dreams/DreamFilterButtons/Filters/ShowThisWeek";
import { LoadDreamsToday } from "source/app/presentation/MainPanel/Dreams/DreamFilterButtons/Filters/ShowToday";
import { IKeyValue } from "motw_core/Entity/IKeyValue";
import { ESharedFunction } from "motw_core/Enums/ESharedFunction";
import { IDreamsState } from "source/app/presentation/MainPanel/Dreams/IDreamsState";
import { ToggleProblemHashTag } from "source/app/presentation/BottomBar/BottomLeftDashboard/Stack/Helpers/ToggleProblemHashTag";
import { problemHashTagHotkeys } from "source/app/presentation/BottomBar/BottomLeftDashboard/Stack/Helpers/ProblemHotkeys";
import { EPanelId } from "source/app/services/Shared/EPanelId";
import { ReadState } from "source/app/services/UI/State/ReadState";
import {
    IKeyboardEvent,
    TKeyboardEvent,
} from "source/app/services/UI/KeyboardEvents/IKeyboardEvents";
import { ESharedComponent } from "source/app/services/UI/State/ESharedComponent";
import { State } from "source/app/services/UI/State/State";

const LoadDreamBoardEvent: IKeyboardEvent = {
    hotkeyDescription: `Ctrl + Alt + b`,
    actionDescription: `Load Dreams this week`,
    trigger: (event: TKeyboardEvent) =>
        event.ctrlKey && event.altKey && event.keyCode == 66,
    conditions: [{ when: EPanelId.Dreams, do: () => LoadDreamsThisWeek() }],
};

const LoadDreamsTodayEvent: IKeyboardEvent = {
    hotkeyDescription: `Ctrl + Alt + t`,
    actionDescription: `Load Today's dreams`,
    trigger: (event: TKeyboardEvent) =>
        event.ctrlKey && event.altKey && event.keyCode == 84,
    conditions: [{ when: EPanelId.Dreams, do: () => LoadDreamsToday() }],
};

const LoadAdjacentDreamsEvent: IKeyboardEvent = {
    hotkeyDescription: `Ctrl + Alt + a`,
    actionDescription: `Load Today's dreams`,
    trigger: (event: TKeyboardEvent) =>
        event.ctrlKey && event.altKey && event.keyCode == 65,
    conditions: [{ when: EPanelId.Dreams, do: () => LoadAdjacentDreams() }],
};

const LoadWaypointDreamsEvent: IKeyboardEvent = {
    hotkeyDescription: "Ctrl + Alt + `",
    actionDescription: `Load waypoint dreams`,
    trigger: (event: TKeyboardEvent) =>
        event.ctrlKey && event.altKey && event.keyCode == 192,
    conditions: [
        {
            when: EPanelId.Dreams,
            do: () => {
                const attributeOnlyFilters = GetAttributeOnlyFilters(
                    EDA.waypoint
                );
                const dreams = State.ReadClass<IDreamsState>(
                    ESharedComponent.Dreams
                );
                dreams.setState(attributeOnlyFilters);
            },
        },
    ],
};

const SearchDreamsEvent: IKeyboardEvent = {
    hotkeyDescription: `Ctrl + Alt + f`,
    actionDescription: `Search`,
    trigger: (event: TKeyboardEvent) =>
        event.ctrlKey && event.altKey && !event.shiftKey && event.keyCode == 70,
    conditions: [
        {
            when: EPanelId.Dreams,
            do: () => {
                jQuery("#target_searchDreams").focus();
            },
        },
    ],
};

const CreateNewProblemEvent: IKeyboardEvent = {
    hotkeyDescription: `Ctrl + Alt + p`,
    actionDescription: `Create new problem`,
    trigger: (event: TKeyboardEvent) =>
        event.ctrlKey && event.altKey && event.keyCode == 80,
    conditions: [
        {
            when: EPanelId.Dreams,
            do: () => {
                const dreams = State.ReadClass<IDreamsState>(
                    ESharedComponent.Dreams
                );

                HandleNewDreamEditorLaunch(undefined, dreams.state);
            },
        },
    ],
};

const GetLoadDreamAttributeEvent = (options: {
    key: string;
    attribute: EDA;
}) => {
    const keycode = options.key.charCodeAt(0);

    return {
        hotkeyDescription: `Ctrl + Alt + ${options.key}`,
        actionDescription: `Load dream ${EDA[options.attribute]}s`,
        trigger: (event: TKeyboardEvent) =>
            event.ctrlKey && event.altKey && event.keyCode == keycode,
        conditions: [
            {
                when: EPanelId.Dreams,
                do: () => LoadDreamsWithAttributeFilter(options.attribute),
            },
        ],
    };
};

const GetActivateProblemHashTagByIndexEvent = (
    index: number,
    key: string
): IKeyboardEvent => {
    const keycode = key.toUpperCase().charCodeAt(0);

    return {
        hotkeyDescription: `Ctrl + Shift + ${key}`,
        actionDescription: `Activate problem #${index}`,
        trigger: (event: TKeyboardEvent) =>
            event.ctrlKey && event.shiftKey && event.keyCode == keycode,
        conditions: [
            {
                when: EPanelId.Dreams,
                do: () => ToggleProblemHashTagByIndex(index),
            },
        ],
    };
};

const ToggleProblemHashTagByIndex = (index: number) => {
    const dreams = State.ReadClass<IDreamsState>(ESharedComponent.Dreams);

    if (!dreams) {
        return;
    }

    const allHashTags = hashTagsService.GetAllDreamHashTags(
        dreams.state.dreamsdata
    );

    const problemHashTags = allHashTags.filter((hashTag) =>
        hashTagsService.IsProblemHashTag(hashTag)
    );

    const sortedProblemHashTags = sortBy(problemHashTags);
    const problemHashTag = sortedProblemHashTags[index];

    if (!problemHashTag) {
        return;
    }

    ToggleProblemHashTag(problemHashTag);
};

const attributeKeyboardEvents: IKeyboardEvent[] = [
    {
        key: `1`,
        attribute: EDA.problem,
    },
    {
        key: `2`,
        attribute: EDA.decision,
    },
    {
        key: `3`,
        attribute: EDA.direction,
    },
    {
        key: `4`,
        attribute: EDA.fascination,
    },
].map((row) => GetLoadDreamAttributeEvent(row));

const problemHashTagKeyboardEvents: IKeyboardEvent[] =
    problemHashTagHotkeys.map((key, index) =>
        GetActivateProblemHashTagByIndexEvent(index, key)
    );

export const dreamKeyboardEvents: IKeyboardEvent[] = [
    LoadDreamBoardEvent,
    LoadDreamsTodayEvent,
    LoadAdjacentDreamsEvent,
    LoadWaypointDreamsEvent,
    SearchDreamsEvent,
    CreateNewProblemEvent,
    ...attributeKeyboardEvents,
    ...problemHashTagKeyboardEvents,
];
